import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { startDirectTask } from '../services/taskService';
import { Zap, Dumbbell, Leaf, Loader2, ArrowRight, CheckCircle2, Search, ArrowUp, ImageIcon } from 'lucide-react';
import { searchHistoryApi } from '../utils/searchHistoryApi';
import { toast } from 'sonner';

// shadcn components
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface QuickStartOption {
  id: string;
  title: string;
  product: string;
  focus: string;
  icon: React.ComponentType<{ className?: string }>;
}

const quickStartOptions: QuickStartOption[] = [
  {
    id: 'byproduct',
    title: "Byproduct Valorization",
    product: "Citrus Processing Waste",
    focus: "Transform orange peels into functional food ingredients",
    icon: Leaf,
  },
  {
    id: 'protein',
    title: "Protein Upcycling",
    product: "Brewery Spent Grain",
    focus: "Convert brewing waste into high-protein flour alternatives",
    icon: Dumbbell,
  },
  {
    id: 'functional',
    title: "Functional Innovation",
    product: "Fruit Processing Byproducts",
    focus: "Extract antioxidants and natural preservatives from fruit waste",
    icon: Zap,
  },
];

// Clean Input Component
interface CleanInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  isValid?: boolean;
  showSearchIcon?: boolean;
  onEnter?: () => void;
  onSearchClick?: () => void;
  isFirstInputValid?: boolean;
}

const CleanInput = ({ id, label, value, onChange, placeholder, disabled, isValid, showSearchIcon, onEnter, onSearchClick, isFirstInputValid }: CleanInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value.trim().length > 0;
  
  // Show arrow only when first input is valid and second input has content
  const shouldShowArrow = showSearchIcon && isFirstInputValid && hasValue;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onEnter && shouldShowArrow) {
      onEnter();
    }
  };

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-medium text-gray-900">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={`
            w-full h-12 px-4 text-sm font-light
            bg-white/80 border-2 rounded-2xl backdrop-blur-sm
            transition-all duration-300 ease-out
            placeholder:text-gray-400 placeholder:font-light
            focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
            ${shouldShowArrow ? 'pr-12' : ''}
            ${isFocused 
              ? 'border-blue-500 shadow-lg shadow-blue-500/20 bg-white' 
              : hasValue && isValid
                ? 'border-gray-300 shadow-md shadow-gray-100/50 bg-white'
                : hasValue
                  ? 'border-gray-300 shadow-md shadow-gray-100/50 bg-white'
                  : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md hover:shadow-gray-100/50 bg-white/60'
            }
          `}
        />
        {shouldShowArrow ? (
          <button
            onClick={onSearchClick}
            disabled={disabled}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-blue-50 transition-colors duration-200 disabled:opacity-50"
          >
            <ArrowUp className="h-4 w-4 text-blue-600" />
          </button>
        ) : (
          !showSearchIcon && hasValue && isValid && (
            <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
          )
        )}
      </div>
    </div>
  );
};

export default function StarterPage() {
  const [productName, setProductName] = useState('');
  const [focusArea, setFocusArea] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(false);
  const navigate = useNavigate();

  const isProductValid = productName.trim().length >= 2;
  const isFocusValid = focusArea.trim().length >= 3;
  const isFormValid = isProductValid && isFocusValid;

  const handleGenerateReport = async () => {
    if (!isFormValid) {
      toast.error("Please complete both fields", {
        description: "Enter a product name and focus area to continue.",
      });
      return;
    }

    setIsLoading(true);
    setProgress(0);
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 15;
      });
    }, 150);

    const payload = {
      product_name: productName,
      reformulation_objective: focusArea,
      generate_image: imageGenerationEnabled,
    };

    console.log('🚀 Starting analysis with payload:', payload);
    if (imageGenerationEnabled) {
      console.log('🎨 Image generation is ENABLED - AI will create product visuals');
    } else {
      console.log('📝 Image generation is DISABLED - text-only analysis');
    }

    try {
      const result = await startDirectTask('food_reformulation', 'run', payload);

      if (result && result.task_id) {
        const taskId = result.task_id;
        setProgress(100);
        
        await searchHistoryApi.addSearchResult(taskId, productName, focusArea);
        
        toast.success("Analysis Started!", {
          description: "Redirecting to your dashboard...",
        });
        
        setTimeout(() => {
          const params = new URLSearchParams({
            product_name: productName,
            reformulation_objective: focusArea
          });
          navigate(`/dashboard/${taskId}?${params.toString()}`);
        }, 1200); 
      } else {
        toast.error("Unable to start analysis", {
          description: "Please try again in a moment.",
        });
        navigate('/dashboard'); 
      }

    } catch (error) {
      console.error('Error starting analysis:', error);
      toast.error("Connection issue", {
        description: "Please check your connection and try again.",
      });
    } finally {
      clearInterval(progressInterval);
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleQuickStart = (option: QuickStartOption) => {
    // Check if this option is already selected
    const isCurrentlySelected = productName === option.product && focusArea === option.focus;
    
    if (isCurrentlySelected) {
      // If already selected, clear the fields (toggle off)
      setProductName('');
      setFocusArea('');
      
      toast.success(`${option.title} deselected`, {
        description: "Fields cleared for new input",
      });
    } else {
      // If not selected, set the fields (toggle on)
      setProductName(option.product);
      setFocusArea(option.focus);
      
      toast.success(`${option.title} selected`, {
        description: "Ready to analyze!",
      });
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden font-sans">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-2xl space-y-12">
          
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <p className="text-base font-light text-gray-600 leading-relaxed max-w-lg mx-auto">
              Revolutionize food product development with AI-powered insights. Transform byproducts into valuable ingredients, optimize formulations, and accelerate innovation while achieving sustainability goals.
            </p>
          </div>

          {/* Main Form Card */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-100/50 hover:shadow-2xl hover:shadow-gray-100/60 transition-all duration-500 ease-out">
            <CardContent className="p-8 space-y-8">
              
              {/* Form Fields */}
              <div className="space-y-6">
                <CleanInput
                  id="productName"
                  label="What product do you want to analyze?"
                  value={productName}
                  onChange={setProductName}
                  placeholder="e.g., Plant-based protein bar, Functional beverage, Sustainable snack"
                  disabled={isLoading}
                  isValid={isProductValid}
                  isFirstInputValid={isProductValid}
                />

                <CleanInput
                  id="focusArea"
                  label="What's your main focus or goal?"
                  value={focusArea}
                  onChange={setFocusArea}
                  placeholder="e.g., upcycle food waste, reduce costs, improve nutrition, enhance sustainability"
                  disabled={isLoading}
                  isValid={isFocusValid}
                  showSearchIcon={true}
                  onEnter={handleGenerateReport}
                  onSearchClick={handleGenerateReport}
                  isFirstInputValid={isProductValid}
                />
              </div>

              {/* Quick Start Options */}
              <div className="space-y-4 pt-2">
                <div className="text-center">
                  <span className="text-xs font-medium text-gray-500 tracking-wide uppercase">
                    Or try a quick start
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {quickStartOptions.map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = productName === option.product && focusArea === option.focus;
                    
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleQuickStart(option)}
                        className={`
                          group p-4 rounded-2xl text-left transition-all duration-300 ease-out
                          hover:scale-[1.02] active:scale-[0.98] border-2
                          ${isSelected 
                            ? 'bg-blue-50/50 border-blue-200/50 shadow-lg shadow-blue-100/50' 
                            : 'bg-gray-50/30 border-gray-100/50 hover:bg-gray-50/50 hover:border-gray-200/50 hover:shadow-lg hover:shadow-gray-100/50'
                          }
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-300
                            ${isSelected 
                              ? 'bg-blue-100/70 border-2 border-blue-200/50' 
                              : 'bg-white/80 border-2 border-gray-100/50 group-hover:bg-white group-hover:border-gray-200/50'
                            }
                          `}>
                            <IconComponent className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-xs ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                              {option.title}
                            </div>
                            <div className={`text-xs font-light ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                              {option.product}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Image Generation Toggle */}
              <div className="space-y-3 pt-2 border-t border-gray-100/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                      <ImageIcon className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <Label htmlFor="image-generation" className="text-sm font-medium text-gray-900">
                        Generate Product Images
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Create visual representations of your product formulations
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="image-generation"
                    checked={imageGenerationEnabled}
                    onCheckedChange={setImageGenerationEnabled}
                    disabled={isLoading}
                  />
                </div>
                {imageGenerationEnabled && (
                  <div className="ml-11 p-3 bg-purple-50/50 rounded-lg border border-purple-100/50">
                    <p className="text-xs text-purple-700">
                      ✨ AI will generate product mockups and formulation visualizations to enhance your analysis
                    </p>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {isLoading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {imageGenerationEnabled ? 'Analyzing formulation & generating images...' : 'Analyzing formulation...'}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {progress}%
                    </span>
                  </div>
                  <div className="relative overflow-hidden rounded-full bg-gray-100 h-2">
                    <div 
                      className={`h-full transition-all duration-500 ease-out rounded-full ${
                        imageGenerationEnabled 
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                          : 'bg-gradient-to-r from-blue-500 to-blue-600'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  {imageGenerationEnabled && (
                    <div className="flex items-center gap-2 text-xs text-purple-600">
                      <ImageIcon className="h-3 w-3" />
                      <span>AI is creating product visualizations...</span>
                    </div>
                  )}
                </div>
              )}

              {/* Form Help Text */}
              <div className="text-center">
                <p className="text-xs font-light text-gray-500">
                  Analysis typically takes 4 to 6 minutes to complete
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 bg-white border-t border-gray-100/50">
        <p className="text-center text-sm font-light text-gray-500">
          &copy; {new Date().getFullYear()} SpyGlass Inc. Pioneering Data Exploration.
        </p>
      </footer>
    </div>
  );
}
